import React, { useState, useEffect, useMemo } from "react";
import { DynamicFieldProps, DynamicFieldItem } from "../../../model/dynamic-forms";
import { Option } from "../../../model/service-request/attribute";
import Select, { SelectItem } from "../../../common/components/new/select/Select";
import { Money } from "../../../model/common/money";
import styles from "./ConditionalDropdown.module.scss";

interface ConditionalDropdownProps extends DynamicFieldProps {}

const ConditionalDropdown: React.FC<ConditionalDropdownProps> = ({ attributeDefinition, fieldValueChanged, preselectedFormValue, isReadOnly = false }) => {
	// Find which parent option contains the preselected child
	const findPreselectedParent = (): Option | null => {
		if (!preselectedFormValue || !preselectedFormValue.fieldValue) {
			return null;
		}

		const preselectedChildId = preselectedFormValue.fieldValue;

		for (const parentOption of attributeDefinition.childrenOptionDefinitions || []) {
			if (parentOption.childOptions) {
				const foundChild = parentOption.childOptions.find(child => child.id === preselectedChildId);
				if (foundChild) {
					return parentOption;
				}
			}
		}

		return null;
	};

	const preselectedParent = findPreselectedParent();
	const [selectedParentOption, setSelectedParentOption] = useState<Option | null>(preselectedParent);

	// Update selected parent when preselectedFormValue changes
	useEffect(() => {
		const parent = findPreselectedParent();
		if (parent) {
			setSelectedParentOption(parent);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preselectedFormValue?.fieldValue]);

	// Generate parent options for first dropdown
	const parentOptions = useMemo(() => {
		return (attributeDefinition.childrenOptionDefinitions || []).map((option: Option) => {
			const priceText = option.price ? ` ${formatPrice(option.price)}` : "";
			return new SelectItem(option.id || option.value, `${option.value}${priceText}`);
		});
	}, [attributeDefinition.childrenOptionDefinitions]);

	// Generate child options for second dropdown based on selected parent
	const childOptions = useMemo(() => {
		if (!selectedParentOption || !selectedParentOption.childOptions) {
			return [];
		}

		return selectedParentOption.childOptions.map((childOption: Option) => {
			const priceText = childOption.price ? ` ${formatPrice(childOption.price)}` : "";
			return new SelectItem(childOption.id || childOption.value, `${childOption.value}${priceText}`);
		});
	}, [selectedParentOption]);

	// Find selected parent item for first dropdown
	const selectedParentItem = useMemo(() => {
		if (!selectedParentOption) return undefined;
		return parentOptions.find(item => item.key === (selectedParentOption.id || selectedParentOption.value));
	}, [selectedParentOption, parentOptions]);

	// Find selected child item for second dropdown (only if it belongs to the selected parent)
	const selectedChildItem = useMemo(() => {
		if (!preselectedFormValue || !preselectedFormValue.fieldValue) return undefined;
		if (!selectedParentOption) return undefined;
		// Only show selected child if it belongs to the currently selected parent
		const childExists = selectedParentOption.childOptions?.some(child => (child.id || child.value) === preselectedFormValue.fieldValue);
		if (!childExists) return undefined;
		return childOptions.find(item => item.key === preselectedFormValue.fieldValue);
	}, [preselectedFormValue, childOptions, selectedParentOption]);

	const handleParentChange = (item: SelectItem | undefined) => {
		if (isReadOnly || !item) return;

		// Find the parent option
		const parentOption = attributeDefinition.childrenOptionDefinitions?.find(opt => (opt.id || opt.value) === item.key) || null;

		setSelectedParentOption(parentOption);
		// Clear child selection when parent changes
		fieldValueChanged({
			fieldId: attributeDefinition.id,
			fieldValue: "",
			fieldType: attributeDefinition.fieldType!,
			fieldLabel: attributeDefinition.placeholderText || attributeDefinition.label,
			fieldPath: `${attributeDefinition.id}.selectedOptionIds[0]`,
		});
	};

	const handleChildChange = (item: SelectItem | undefined) => {
		if (isReadOnly || !item) return;

		if (!selectedParentOption) return;

		// Construct fieldPath for child option
		const fieldPath = `${attributeDefinition.id}.selectedOptionIds[0]`;

		fieldValueChanged({
			fieldId: attributeDefinition.id,
			fieldValue: item.key,
			dropdownText: item.label,
			fieldType: attributeDefinition.fieldType!,
			fieldLabel: attributeDefinition.placeholderText || attributeDefinition.label,
			fieldPath: fieldPath,
			selectedChildId: item.key,
		});
	};

	const fieldLabel = attributeDefinition.placeholderText || attributeDefinition.label;

	return (
		<div className={styles.conditionalDropdown}>
			<Select
				label={`${fieldLabel} (I)`}
				placeholder="Select parent option"
				items={parentOptions}
				selected={selectedParentItem}
				required={attributeDefinition.isRequired}
				onChange={handleParentChange}
			/>
			<Select
				label={`${fieldLabel} (II)`}
				placeholder={selectedParentOption ? "Select option" : `Select '${fieldLabel} (I)' first`}
				items={childOptions}
				selected={selectedChildItem}
				required={attributeDefinition.isRequired}
				onChange={handleChildChange}
			/>
		</div>
	);
};

// Helper function to format price
const formatPrice = (price: Money): string => {
	if (!price) return "";
	return price.toLocaleString();
};

export default ConditionalDropdown;
